export class Analytics {
	instance = null;
	dataLayer = null;
	adobeDataLayer = null;
	localItem = {};

	constructor() {
		const [dataLayer, adobeDataLayer] = Analytics._getAnalyticsResources();
		this.dataLayer = dataLayer;
		this.adobeDataLayer = adobeDataLayer;
	}

	static storeType = {
		OMNI: 'Omni',
	};

	static eventType = {
		PRODUCT_VIEW: 'productView',
		CART_VIEW: 'cartView',
		CART_REMOVE: 'cartRemove',
		CART_ADD: 'cartAdd',
		CART_ADD_OPEN: 'cartAddOpen',
		CHECKOUT_START: 'checkoutStart',
		ERROR: 'errorMessage',
	};

	static dataType = {
		PRODUCT: 'product-pricing',
		BUNDLE: 'bundle-product-pricing',
		CART_DELTA: 'cart-delta',
		CART: 'cart',
	};

	static getInstance() {
		if (!this.instance) {
			this.instance = new Analytics();
		}

		return this.instance;
	}

	static _getAnalyticsResources() {
		let dataLayer = window.dataLayer;
		let adobeDataLayer = window.adobeDataLayer;

		if (dataLayer == null) {
			dataLayer = {};
			console.warn('Unable to find data layer');
		}
		if (adobeDataLayer == null) {
			adobeDataLayer = [];
			console.warn('Unable to find adobe data layer');
		}

		return [dataLayer, adobeDataLayer];
	}

	static toItems(dataList, dataType, options = {}) {
		if (dataType === Analytics.dataType.BUNDLE) {
			return this._bundleDataListToItems(dataList, options);
		} else if (dataType === Analytics.dataType.PRODUCT) {
			return this._productPricingDataListToItems(dataList);
		} else if (dataType === Analytics.dataType.CART_DELTA) {
			return this._cartDeltaDataListToItems(dataList, options);
		} else if (dataType === Analytics.dataType.CART) {
			return this._cartDataListToItems(dataList);
		}
	}

	static _bundleDataListToItems(dataList, options = {}) {
		if (!dataList) return [];

		const { includeQty } = options;
		const skuToItem = new Map();
		dataList.forEach((data) => {
			if (!data?.prodConfig?.data) return;

			data.prodConfig.data.forEach((product) => {
				const sku = product?.SKU || product?.sku;
				const item = this._productDataToItem(product, skuToItem.get(sku), data.prodConfig, data.bundleInfo, includeQty);
				skuToItem.set(sku, item);
			});
		});

		return Array.from(skuToItem.values());
	}

	static _productPricingDataListToItems(dataList) {
		if (!dataList) return [];

		const items = [];
		dataList.forEach((data) => {
			if (!data?.productPricingData) return;

			data.productPricingData.forEach((productPricingData) => {
				if (!productPricingData?.products.length) return;

				productPricingData.products.forEach((product) => {
					const item = Analytics._productToItem(product, data.tabTitle);
					items.push(item);
				});
			});
		});

		return items;
	}

	static _cartDeltaDataListToItems(dataList, options = {}) {
		const [origCart, cart] = dataList;
		if (!cart?.lineItems?.length) return [];

		const items = [];
		cart.lineItems.forEach((lineItem) => {
			const origLineItem = origCart?.lineItems?.find((origLineItem) => origLineItem?._id === lineItem._id);
			if (origLineItem?.quantity === lineItem?.quantity) return;

			const item = this._lineItemToItem(lineItem, {
				currency: cart?.currency,
				quantity: lineItem.quantity - (origLineItem?.quantity || 0),
				bundle: options.bundle,
			});
			items.push(item);
		});

		return items;
	}

	static _cartDataListToItems(dataList) {
		const [cart] = dataList;
		if (!cart?.lineItems?.length) return [];

		const items = [];
		cart.lineItems.forEach((lineItem) => {
			const item = this._lineItemToItem(lineItem, {
				currency: cart?.currency,
			});
			items.push(item);
		});

		return items;
	}

	static _productToItem(product, tabTitle) {
		const productInfo = {
			sku: product.isFree && !product.dataLayerSku ? product.productTitle : product.dataLayerSku,
			name: product.productTitle,
			currency: product.currency,
			price: Analytics._formatPrice(product.displayPrice),
		};

		return {
			productInfo: productInfo,
			...(tabTitle && { tabTitle }),
		};
	}

	static _productDataToItem(product, existingItem, prodConfig, bundleInfo, includeQty) {
		const prevProductInfo = existingItem?.productInfo;
		const productInfo = {
			sku: prevProductInfo?.sku || product.SKU || product.sku,
			name: prevProductInfo?.name || product.productTitle || product.productLable,
			price: prevProductInfo?.price || Analytics._formatPrice(product.price),
			currency: prevProductInfo?.currency || prodConfig?.currency,
			bundle: (prevProductInfo?.bundle || '') + (prevProductInfo?.bundle ? '|' : '') + (bundleInfo?.title || ''),
			...(includeQty && product.qty && { quantity: product.qty }),
		};

		return { productInfo };
	}

	static _lineItemToItem(lineItem, options = {}) {
		const productInfo = {
			sku: lineItem.materialNumber?.toString(),
			name: lineItem.productName,
			price: lineItem.price,
			currency: options.currency,
			quantity: options.quantity || lineItem.quantity,
			...(options.bundle && { bundle: options.bundle }),
		};

		return { productInfo };
	}

	static _formatPrice(price) {
		// Free or Contact Us products have undefined for price, change it to "0".
		return price ? price.replace(/[^0-9.-]+/g, '') : '0';
	}

	static toStoreFront(zone) {
		return zone ? `${zone.toLowerCase()} - store` : null;
	}

	addPageInfo(pageData) {
		Object.assign(this.dataLayer, pageData);
	}

	addEvent(name, data) {
		this.adobeDataLayer.push({
			event: name,
			data: data,
		});
	}

	getPageItem(item) {
		return this.dataLayer[item];
	}

	setLocalItem(key, value) {
		this.localItem[key] = value;
	}

	getLocalItem(key) {
		return this.localItem[key];
	}
}
